import { NavLink, Outlet, useLocation, useParams, Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LayoutDashboard, CalendarDays, Users, Ticket, Megaphone, FilePenLine,
  IdCard, ScanLine, BarChart3, Wallet, ScrollText, Settings as SettingsIcon, Images,
  Search, Plus, Bell, ChevronDown, LogOut,   LayoutTemplate, Smartphone, PanelLeftClose, PanelLeftOpen, X, Menu,
  CircleHelp,
  Sparkles,
  LayoutGrid,
} from "lucide-react";
import AdminAiSearchPanel from "@/components/AdminAiSearchPanel";
import AivoryOneDock from "@/components/AivoryOneDock";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";
import TenantLogo from "@/components/TenantLogo";
import CoBrandLogo from "@/components/CoBrandLogo";
import { useTenantBrand } from "@/components/TenantBrandProvider";
import { isLightBackground } from "@/lib/tenant-brand";
import MaintenanceNoticeBanner from "@/components/MaintenanceNoticeBanner";
import { useTheme } from "@/components/ThemeProvider";
import usePublicStatus from "@/hooks/usePublicStatus";
import { API_BASE } from "@/lib/utils";
import { performSignOut, redirectToLogin, saveAuthSession } from "@/lib/auth-session";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { clearBootstrapCache, loadBootstrapData } from "@/lib/bootstrap-cache";
import { eventNavLabel } from "@/lib/event-display";
import { useEventMeta } from "@/lib/event-meta";
import { AdminEventsContext } from "@/contexts/AdminEventsContext";
import { toast } from "sonner";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

const topNav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/events", label: "Events", icon: CalendarDays },
  { to: "/admin/media", label: "Media", icon: Images },
  { to: "/admin/finance", label: "Finance", icon: Wallet },
  { to: "/admin/reports/registrations", label: "Registrations", icon: BarChart3 },
  { to: "/admin/audit", label: "Audit", icon: ScrollText },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
  { to: "/admin/help", label: "Get help", icon: CircleHelp },
  { to: "/admin/ai", label: "AI", icon: Sparkles },
];

function readAuthSession() {
  try {
    const raw = localStorage.getItem("aivoryems.auth.session");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function operatorInitials(email, name) {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
  }
  const local = String(email || "").split("@")[0] || "";
  const bits = local.split(/[._-]+/).filter(Boolean);
  if (bits.length >= 2) return `${bits[0][0]}${bits[1][0]}`.toUpperCase();
  return local.slice(0, 2).toUpperCase() || "?";
}

function operatorDisplayName(email, name) {
  if (name) return name;
  const local = String(email || "").split("@")[0] || "Operator";
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const eventNav = (id) => [
  { to: `/admin/events/${id}`, label: "Overview", icon: LayoutDashboard, end: true },
  { to: `/admin/events/${id}/attendees`, label: "Attendees", icon: Users },
  { to: `/admin/events/${id}/seats`, label: "Seat arrangement", icon: LayoutGrid },
  { to: `/admin/events/${id}/tickets`, label: "Tickets", icon: Ticket },
  { to: `/admin/events/${id}/communications`, label: "Comms", icon: Megaphone },
  { to: `/admin/events/${id}/landing`, label: "Page Builder", icon: LayoutTemplate, feature: "page_builder" },
  { to: `/admin/events/${id}/form`, label: "Form Builder", icon: FilePenLine, feature: "form_builder" },
  { to: `/admin/events/${id}/badge`, label: "Badge", icon: IdCard },
  { to: `/admin/events/${id}/wallet`, label: "Wallet Pass", icon: Smartphone },
  { to: `/admin/events/${id}/checkin`, label: "Live Check-in", icon: ScanLine },
  { to: `/admin/events/${id}/reports`, label: "Reports", icon: BarChart3 },
];

const FEATURE_UPGRADE_LABEL = {
  page_builder: "Studio",
  form_builder: "Starter",
  api_access: "Scale",
  webhooks: "Scale",
  whitelabel: "Enterprise",
  ai_search: "Studio",
};

export default function AdminLayout() {
  const { setTheme } = useTheme();
  const { brand, workspaceName } = useTenantBrand();
  const displayWorkspace = workspaceName || brand.productName || "Workspace";
  const SidebarBrand = brand.enabled && brand.mode === "cobrand" ? CoBrandLogo : TenantLogo;
  const showPoweredBy = brand.enabled && brand.mode === "cobrand" && brand.showPoweredBy;
  const { banners } = usePublicStatus();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const isEventScope = pathname.startsWith("/admin/events/") && params.id;
  const isImmersiveEditor = /^\/admin\/events\/[^/]+\/landing$/.test(pathname);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [regions, setRegions] = useState(0);
  const [globalQuery, setGlobalQuery] = useState("");
  const [apiSearchItems, setApiSearchItems] = useState([]);
  const searchInputRef = useRef(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [aiSearchOpen, setAiSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notificationItems, setNotificationItems] = useState([]);
  const [operatorProfile, setOperatorProfile] = useState({
    email: "",
    name: "Operator",
    initials: "?",
  });
  const [capabilities, setCapabilities] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [membershipsLoading, setMembershipsLoading] = useState(true);
  const [switchingTenant, setSwitchingTenant] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("aivoryems.ui.sidebarCollapsed") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const session = readAuthSession();
    if (!session?.email || !session?.token) {
      redirectToLogin();
      return;
    }
    const email = String(session.email).trim();
    let cancelled = false;
    (async () => {
      let rosterName = "";
      try {
        const data = await loadBootstrapData();
        const lower = email.toLowerCase();
        const roster = [
          ...(Array.isArray(data?.team) ? data.team : []),
          ...(Array.isArray(data?.teamInvites) ? data.teamInvites : []),
        ];
        const match = roster.find((row) => String(row?.email || "").toLowerCase() === lower);
        rosterName = String(match?.name || "").trim();
      } catch {
        // fall back to email-derived display name
      }
      if (cancelled) return;
      setOperatorProfile({
        email,
        name: operatorDisplayName(email, rosterName),
        initials: operatorInitials(email, rosterName),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMembershipsLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/admin/memberships`, { headers: getAuthHeaders() });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.detail || String(res.status));
        if (!cancelled) {
          setMemberships(Array.isArray(body?.memberships) ? body.memberships : []);
        }
      } catch {
        if (!cancelled) setMemberships([]);
      } finally {
        if (!cancelled) setMembershipsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const switchWorkspace = useCallback(async (tenantId) => {
    const session = readAuthSession();
    const currentTenant = String(session?.tenant || "").trim();
    if (!tenantId || tenantId === currentTenant) return;
    setSwitchingTenant(tenantId);
    try {
      const res = await fetch(`${API_BASE}/api/admin/switch-workspace`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "content-type": "application/json" },
        body: JSON.stringify({ tenantId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `Switch failed (${res.status})`);
      if (!body?.token) throw new Error("No session token returned");
      clearBootstrapCache();
      saveAuthSession({
        email: body.email || session?.email,
        token: body.token,
        role: body.role || session?.role || "admin",
        tenant: body.tenant || tenantId,
        via: session?.via || "workspace-switch",
      });
      toast.success("Workspace switched");
      window.location.assign("/admin");
    } catch (err) {
      toast.error("Could not switch workspace", { description: err?.message || "Try again." });
      setSwitchingTenant("");
    }
  }, []);

  // Respect workspace org.theme; only infer from brand when no saved preference exists.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/state/workspace.settings`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) return;
        const payload = await res.json();
        const t = String(payload?.value?.org?.theme || "").toLowerCase();
        if (!cancelled && (t === "light" || t === "dark")) {
          setTheme(t, true);
          return;
        }
        if (brand?.enabled && brand?.colors?.background) {
          setTheme(isLightBackground(brand.colors.background) ? "light" : "dark", true);
        }
      } catch {
        // no-op -- ThemeProvider's localStorage default stays in effect
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brand, setTheme]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/capabilities`, {
          cache: "no-store",
          headers: getAuthHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return;
        if (!cancelled) setCapabilities(data);
      } catch {
        // optional
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const featureEnabled = useCallback(
    (key) => {
      if (!key) return true;
      if (!capabilities) return true;
      if (capabilities.grandfathered === true) return true;
      const flags = capabilities.featureFlags || {};
      if (flags[key] === false) return false;
      if (flags[key] === true) return true;
      return (capabilities.features || []).includes(key);
    },
    [capabilities],
  );

  const onLockedFeature = useCallback((feature) => {
    const plan = FEATURE_UPGRADE_LABEL[feature] || "Studio";
    toast.message(`Upgrade to ${plan}`, {
      description: "This capability requires a higher plan.",
      action: { label: "Billing", onClick: () => navigate("/admin/billing") },
    });
  }, [navigate]);

  const refreshEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/events`, {
        cache: "no-store",
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to load events (${res.status})`);
      const payload = await res.json();
      const list = Array.isArray(payload?.events) ? payload.events : [];
      setEvents(list);
      setRegions(Number(payload?.regions) || 0);
    } catch {
      setEvents([]);
      setRegions(0);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshEvents();
  }, [refreshEvents]);

  useEffect(() => {
    const q = globalQuery.trim();
    if (q.length < 1) {
      setApiSearchItems([]);
      return undefined;
    }
    const t = window.setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/search?q=${encodeURIComponent(q)}`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) return;
        const payload = await res.json();
        setApiSearchItems(Array.isArray(payload?.items) ? payload.items : []);
      } catch {
        setApiSearchItems([]);
      }
    }, 180);
    return () => window.clearTimeout(t);
  }, [globalQuery]);

  const aiSearchEnabled = featureEnabled("ai_search");

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === "k") {
        e.preventDefault();
        setAiSearchOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("aivoryems.ui.sidebarCollapsed", sidebarCollapsed ? "1" : "0");
    } catch {
      // no-op
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    setMobileSearchOpen(false);
    setAiSearchOpen(false);
    setMobileNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/notifications`);
        if (!res.ok) return;
        const payload = await res.json();
        if (!cancelled) setNotificationItems(Array.isArray(payload?.items) ? payload.items : []);
      } catch {
        if (!cancelled) setNotificationItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  const eventMeta = useEventMeta(isEventScope ? params.id : "");
  const evt = useMemo(() => {
    if (!isEventScope) return null;
    const id = String(params.id || "");
    const fromList = events.find((row) => row.id === id || row.slug === id);
    const merged = fromList || (eventMeta?.id || eventMeta?.title ? eventMeta : null);
    if (merged) return { ...merged, id: merged.id || id };
    return { id, title: "", handle: "", venue: "" };
  }, [isEventScope, params.id, events, eventMeta]);
  const evtNavLabel = evt ? eventNavLabel(evt) : "";
  const searchResults = useMemo(() => {
    const q = globalQuery.trim().toLowerCase();
    if (!q) return [];
    if (apiSearchItems.length > 0) {
      return apiSearchItems.map((row) => ({
        id: `${row.kind || "item"}-${row.id || row.href}`,
        title: row.title || "Result",
        subtitle: row.subtitle || "",
        to: row.href || "/admin",
        ai: Boolean(row.ai),
      }));
    }
    const eventMatches = events
      .filter((row) =>
        `${row.title || ""} ${row.handle || ""} ${row.venue || ""} ${row.slug || ""}`.toLowerCase().includes(q),
      )
      .slice(0, 6)
      .map((row) => ({
        id: row.id,
        title: eventNavLabel(row),
        subtitle: row.venue || row.id || "Event",
        to: `/admin/events/${row.id}`,
      }));
    const staticMatches = [
      { id: "nav-finance", title: "Finance", subtitle: "Workspace", to: "/admin/finance" },
      { id: "nav-audit", title: "Audit Log", subtitle: "Workspace", to: "/admin/audit" },
      { id: "nav-settings", title: "Settings", subtitle: "Workspace", to: "/admin/settings" },
    ].filter((row) => `${row.title} ${row.subtitle}`.toLowerCase().includes(q));
    return [...eventMatches, ...staticMatches].slice(0, 8);
  }, [events, globalQuery, apiSearchItems]);
  const notifications = useMemo(() => {
    return (Array.isArray(notificationItems) ? notificationItems : []).slice(0, 6).map((row, idx) => ({
      id: row?.id || `n_${idx}`,
      title: row?.title || "System activity",
      subtitle: row?.body || row?.target || row?.actor || "AivoryEMS",
      ts: row?.ts || "",
      read: Boolean(row?.read),
    }));
  }, [notificationItems]);
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="h-screen overflow-hidden flex flex-col" style={{ background: "var(--bg-base)" }}>
      {!isImmersiveEditor ? <MaintenanceNoticeBanner banners={banners} /> : null}
      <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* Sidebar — hidden in immersive editors (Page Builder) */}
      {!isImmersiveEditor ? (
      <aside className={`${sidebarCollapsed ? "w-[84px]" : "w-[260px]"} shrink-0 border-r border-[var(--border-default)] hidden md:flex flex-col transition-all duration-200`} data-testid="admin-sidebar">
        <div className={`${sidebarCollapsed ? "px-3 justify-center" : "px-6"} h-16 flex items-center border-b border-[var(--border-default)]`}>
          <Link to="/admin" className="flex items-center gap-2" data-testid="brand-link">
            <SidebarBrand showWordmark={!sidebarCollapsed} />
          </Link>
        </div>

        <nav className={`flex-1 overflow-y-auto py-6 ${sidebarCollapsed ? "px-2" : "px-3"} space-y-6`}>
          <div>
            {!sidebarCollapsed ? <div className="text-overline px-3 mb-2">Workspace</div> : null}
            <div className="space-y-0.5">
              {topNav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center ${sidebarCollapsed ? "justify-center" : "gap-3"} px-3 py-2 text-sm transition-all border-l-2 ${
                      isActive
                        ? "text-[var(--text-primary)] border-[var(--primary)] bg-[var(--bg-surface)]"
                        : "text-[var(--text-secondary)] border-transparent hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)]"
                    }`
                  }
                  title={sidebarCollapsed ? n.label : undefined}
                >
                  <n.icon size={16} strokeWidth={1.6} />
                  {!sidebarCollapsed ? <span>{n.label}</span> : null}
                </NavLink>
              ))}
            </div>
          </div>

          {evt && !sidebarCollapsed && (
            <div className="fade-up">
              <div className="px-3 mb-2 min-w-0">
                <div className="text-sm font-medium text-[var(--text-primary)] truncate" title={evtNavLabel}>
                  {evtNavLabel || "Loading…"}
                </div>
                {evt?.id ? (
                  <div className="text-[10px] font-mono text-[var(--text-tertiary)] truncate mt-0.5" title={evt.id}>
                    {evt.id}
                  </div>
                ) : null}
              </div>
              <div className="space-y-0.5">
                {eventNav(evt.id).map((n) => {
                  const locked = n.feature && !featureEnabled(n.feature);
                  if (locked) {
                    return (
                      <button
                        key={n.to}
                        type="button"
                        className="flex w-full items-center gap-3 px-3 py-2 text-sm text-[var(--text-tertiary)] border-l-2 border-transparent opacity-70 hover:opacity-100"
                        onClick={() => onLockedFeature(n.feature)}
                      >
                        <n.icon size={15} strokeWidth={1.6} />
                        <span>{n.label}</span>
                      </button>
                    );
                  }
                  return (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      data-testid={`event-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 text-sm transition-all border-l-2 ${
                          isActive
                            ? "text-[var(--on-primary)] bg-[var(--primary)] border-[var(--primary)] font-medium"
                            : "text-[var(--text-secondary)] border-transparent hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)]"
                        }`
                      }
                    >
                      <n.icon size={15} strokeWidth={1.6} />
                      <span>{n.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          )}
        </nav>

        <div className={`${sidebarCollapsed ? "p-2" : "p-3"} border-t border-[var(--border-default)] space-y-2`}>
          {showPoweredBy && !sidebarCollapsed ? (
            <div className="px-3 pb-1 text-[10px] font-mono text-[var(--text-tertiary)] text-center">
              Powered by{" "}
              <a href="https://aivoryems.com" className="text-[var(--primary)] hover:underline" target="_blank" rel="noreferrer">
                AivoryEMS
              </a>
            </div>
          ) : null}
          <button
            type="button"
            className={`w-full btn-ghost ${sidebarCollapsed ? "justify-center !px-0" : "justify-start"} border border-[var(--border-default)] hover:border-[var(--primary)]`}
            onClick={() => setSidebarCollapsed((v) => !v)}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="toggle-sidebar-btn-side"
          >
            {sidebarCollapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
            {!sidebarCollapsed ? <span className="text-xs font-mono uppercase tracking-widest">Collapse menu</span> : null}
          </button>
          <div className={`${sidebarCollapsed ? "justify-center px-0" : "px-3"} py-2 text-[11px] font-mono text-[var(--text-tertiary)] flex items-center gap-2`}>
            <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full pulse-dot" />
            {!sidebarCollapsed ? "ALL SYSTEMS OPERATIONAL" : null}
          </div>
        </div>
      </aside>
      ) : null}

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          className="!w-[280px] !p-0 !gap-0 !bg-[var(--bg-base)] border-[var(--border-default)] flex flex-col"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="h-16 px-6 flex items-center border-b border-[var(--border-default)]">
            <Link to="/admin" className="flex items-center gap-2" onClick={() => setMobileNavOpen(false)}>
              <SidebarBrand showWordmark />
            </Link>
          </div>
          <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-6">
            <div>
              <div className="text-overline px-3 mb-2">Workspace</div>
              <div className="space-y-0.5">
                {topNav.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={() => setMobileNavOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 text-sm transition-all border-l-2 ${
                        isActive
                          ? "text-[var(--text-primary)] border-[var(--primary)] bg-[var(--bg-surface)]"
                          : "text-[var(--text-secondary)] border-transparent hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)]"
                      }`
                    }
                  >
                    <n.icon size={16} strokeWidth={1.6} />
                    <span>{n.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
            {evt && (
              <div>
                <div className="text-overline px-3 mb-2 truncate">{evt.title}</div>
                <div className="space-y-0.5">
                  {eventNav(evt.id).map((n) => {
                    const locked = n.feature && !featureEnabled(n.feature);
                    if (locked) {
                      return (
                        <button
                          key={n.to}
                          type="button"
                          className="flex w-full items-center gap-3 px-3 py-2 text-sm text-[var(--text-tertiary)] border-l-2 border-transparent opacity-70"
                          onClick={() => {
                            setMobileNavOpen(false);
                            onLockedFeature(n.feature);
                          }}
                        >
                          <n.icon size={15} strokeWidth={1.6} />
                          <span>{n.label}</span>
                        </button>
                      );
                    }
                    return (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        onClick={() => setMobileNavOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 text-sm transition-all border-l-2 ${
                            isActive
                              ? "text-[var(--on-primary)] bg-[var(--primary)] border-[var(--primary)] font-medium"
                              : "text-[var(--text-secondary)] border-transparent hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)]"
                          }`
                        }
                      >
                        <n.icon size={15} strokeWidth={1.6} />
                        <span>{n.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            )}
          </nav>
          <div className="p-3 border-t border-[var(--border-default)]">
            <div className="px-3 py-2 text-[11px] font-mono text-[var(--text-tertiary)] flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full pulse-dot" />
              ALL SYSTEMS OPERATIONAL
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex-1 min-h-0 flex flex-col min-w-0">
        {/* Topbar — hidden in immersive editors */}
        {!isImmersiveEditor ? (
        <header className="h-16 border-b border-[var(--border-default)] px-4 md:px-8 flex items-center justify-between gap-4" data-testid="admin-topbar">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="btn-ghost !px-2.5 shrink-0 md:hidden"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation"
              data-testid="mobile-nav-btn"
            >
              <Menu size={16} />
            </button>
            <button
              type="button"
              className="btn-ghost !px-2.5 shrink-0 hidden md:inline-flex"
              onClick={() => setSidebarCollapsed((v) => !v)}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              data-testid="toggle-sidebar-btn"
            >
              {sidebarCollapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
            </button>
            <div className="hidden md:flex items-center gap-3 min-w-0">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition min-w-0" data-testid="workspace-switcher">
                <span className="font-heading text-[var(--text-primary)] font-medium truncate">{displayWorkspace}</span>
                <ChevronDown size={14} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[var(--bg-surface)] border-[var(--border-default)] rounded-none min-w-[14rem]">
                <DropdownMenuLabel className="text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">Workspaces</DropdownMenuLabel>
                {membershipsLoading ? (
                  <DropdownMenuItem className="rounded-none text-[var(--text-tertiary)]" disabled>
                    Loading…
                  </DropdownMenuItem>
                ) : memberships.length === 0 ? (
                  <DropdownMenuItem className="rounded-none">{displayWorkspace}</DropdownMenuItem>
                ) : (
                  memberships.map((m) => {
                    const tid = String(m.tenantId || "");
                    const label = String(m.workspaceName || m.organizationName || tid);
                    const isCurrent = tid && tid === String(readAuthSession()?.tenant || "");
                    return (
                      <DropdownMenuItem
                        key={tid || label}
                        className="rounded-none"
                        disabled={Boolean(switchingTenant) || isCurrent}
                        onSelect={() => switchWorkspace(tid)}
                      >
                        {label}
                        {isCurrent ? " · current" : switchingTenant === tid ? " …" : ""}
                      </DropdownMenuItem>
                    );
                  })
                )}
                <DropdownMenuSeparator className="bg-[var(--border-default)]" />
                <DropdownMenuItem className="rounded-none text-[var(--text-tertiary)]" disabled>
                  Additional workspaces — contact support
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="text-[var(--border-hover)]">/</span>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition min-w-0" data-testid="event-switcher">
                <span className="text-[var(--text-primary)] truncate">{evt ? evtNavLabel : "All events"}</span>
                <ChevronDown size={14} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[var(--bg-surface)] border-[var(--border-default)] rounded-none w-64">
                <DropdownMenuLabel className="text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">Switch event</DropdownMenuLabel>
                {events.map((e) => (
                  <DropdownMenuItem key={e.id} className="rounded-none flex flex-col items-start" asChild>
                    <Link to={`/admin/events/${e.id}`}>
                      <span>{e.title}</span>
                      <span className="text-[10px] text-[var(--text-tertiary)] font-mono">{e.venue}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
                {!eventsLoading && events.length === 0 ? (
                  <DropdownMenuItem className="rounded-none text-[var(--text-tertiary)]" disabled>
                    No events found
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              className="btn-secondary !px-2.5 hidden sm:inline-flex items-center gap-1.5"
              onClick={() => setAiSearchOpen(true)}
              data-testid="ai-search-open"
              title="AI Search (⌘K)"
            >
              <Sparkles size={14} className={aiSearchEnabled ? "text-[var(--primary)]" : ""} />
              <span className="hidden md:inline text-xs">AI</span>
            </button>
            <div className="relative hidden lg:block">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none" aria-hidden />
              <input
                ref={searchInputRef}
                placeholder={aiSearchEnabled ? "Quick search · ⌘K for AI" : "Search events, attendees…"}
                data-testid="global-search"
                value={globalQuery}
                onChange={(e) => setGlobalQuery(e.target.value)}
                className="search-input search-input--kbd bg-[var(--bg-surface)] border border-[var(--border-default)] text-sm py-2 w-[320px] focus:outline-none focus:border-[var(--primary)] transition"
              />
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-[var(--text-tertiary)] border border-[var(--border-default)] px-1.5 py-0.5">⌘K</kbd>
              {globalQuery.trim() ? (
                <div className="absolute top-[110%] left-0 right-0 bg-[var(--bg-surface)] border border-[var(--border-default)] z-30 max-h-72 overflow-auto">
                  {searchResults.map((row) => (
                    <Link
                      key={row.id}
                      to={row.to}
                      className="block px-3 py-2 border-b border-[var(--border-default)] hover:bg-[var(--bg-elev)]"
                      onClick={() => setGlobalQuery("")}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[var(--text-primary)]">{row.title}</span>
                        {row.ai ? (
                          <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[var(--primary)]/40 text-[var(--primary)]">
                            AI
                          </span>
                        ) : null}
                      </div>
                      <div className="text-[10px] font-mono text-[var(--text-tertiary)]">{row.subtitle}</div>
                    </Link>
                  ))}
                  {searchResults.length === 0 ? <div className="px-3 py-2 text-xs text-[var(--text-tertiary)]">No matching records.</div> : null}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="btn-ghost !px-2.5 lg:hidden"
              onClick={() => setAiSearchOpen(true)}
              data-testid="mobile-search-btn"
              aria-label="AI Search"
            >
              <Sparkles size={14} className={aiSearchEnabled ? "text-[var(--primary)]" : ""} />
            </button>
            <Link to="/admin/events/new" className="btn-primary" data-testid="new-event-btn">
              <Plus size={14} /> <span className="hidden sm:inline">New event</span>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger className="btn-secondary !px-3 relative" data-testid="notifications-btn">
                <Bell size={14} />
                {unreadCount > 0 ? (
                  <span className="absolute -top-1 -right-1 text-[10px] font-mono min-w-[16px] h-4 px-1 bg-[var(--primary)] text-[var(--on-primary)] inline-flex items-center justify-center">
                    {Math.min(unreadCount, 9)}
                  </span>
                ) : null}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[var(--bg-surface)] border-[var(--border-default)] rounded-none w-80">
                <DropdownMenuLabel className="text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">Notifications</DropdownMenuLabel>
                {notifications.map((n) => (
                  <DropdownMenuItem
                    key={n.id}
                    className={`rounded-none flex flex-col items-start ${n.read ? "opacity-70" : ""}`}
                    onClick={async () => {
                      if (n.read) return;
                      try {
                        await fetch(`${API_BASE}/api/admin/notifications/${n.id}/read`, { method: "POST" });
                        setNotificationItems((rows) => rows.map((r) => (String(r?.id || "") === String(n.id) ? { ...r, read: true } : r)));
                      } catch {
                        // no-op
                      }
                    }}
                  >
                    <span className="text-sm">{n.title}</span>
                    <span className="text-[10px] font-mono text-[var(--text-tertiary)]">{n.subtitle}</span>
                    <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1">{n.ts}</span>
                  </DropdownMenuItem>
                ))}
                {notifications.length === 0 ? (
                  <DropdownMenuItem className="rounded-none text-[var(--text-tertiary)]" disabled>
                    No notifications yet
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator className="bg-[var(--border-default)]" />
                <DropdownMenuItem className="rounded-none p-0 focus:bg-transparent">
                  <Link to="/admin/notifications" className="block w-full px-2 py-2 text-[var(--primary)] text-xs font-mono uppercase tracking-widest hover:bg-[var(--bg-elev)]">
                    View all notifications
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="profile-trigger">
                <Avatar className="w-9 h-9 rounded-none border border-[var(--border-default)]">
                  <AvatarFallback className="bg-[var(--primary)] text-[var(--on-primary)] rounded-none font-medium text-xs">
                    {operatorProfile.initials}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[var(--bg-surface)] border-[var(--border-default)] rounded-none w-56">
                <DropdownMenuLabel>
                  <div className="text-sm">{operatorProfile.name}</div>
                  <div className="text-[10px] text-[var(--text-tertiary)] font-mono">{operatorProfile.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[var(--border-default)]" />
                <DropdownMenuItem className="rounded-none" asChild><Link to="/admin/profile">Profile & workspaces</Link></DropdownMenuItem>
                <DropdownMenuItem className="rounded-none" asChild><Link to="/admin/help">Get help</Link></DropdownMenuItem>
                <DropdownMenuItem className="rounded-none" asChild><Link to="/admin/settings">Workspace settings</Link></DropdownMenuItem>
                <DropdownMenuItem className="rounded-none" asChild><Link to="/admin/billing">Billing</Link></DropdownMenuItem>
                <DropdownMenuItem className="rounded-none" asChild><Link to="/admin/api-keys">API keys</Link></DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[var(--border-default)]" />
                <DropdownMenuItem
                  className="rounded-none flex items-center gap-2"
                  onClick={() => performSignOut()}
                >
                  <LogOut size={14} /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        ) : null}

        {mobileSearchOpen && !isImmersiveEditor ? (
          <div className="lg:hidden border-b border-[var(--border-default)] px-4 py-3" style={{ background: "var(--bg-base)" }}>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none" aria-hidden />
              <input
                placeholder="Search events, attendees, orders…"
                data-testid="global-search-mobile"
                value={globalQuery}
                onChange={(e) => setGlobalQuery(e.target.value)}
                autoFocus
                className="search-input search-input--trailing bg-[var(--bg-surface)] border border-[var(--border-default)] text-sm py-2 w-full focus:outline-none focus:border-[var(--primary)] transition"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                onClick={() => { setMobileSearchOpen(false); setGlobalQuery(""); }}
              >
                <X size={14} />
              </button>
              {globalQuery.trim() ? (
                <div className="absolute top-full mt-1 left-0 right-0 bg-[var(--bg-surface)] border border-[var(--border-default)] z-30 max-h-72 overflow-auto">
                  {searchResults.map((row) => (
                    <Link
                      key={row.id}
                      to={row.to}
                      className="block px-3 py-2 border-b border-[var(--border-default)] hover:bg-[var(--bg-elev)]"
                      onClick={() => { setGlobalQuery(""); setMobileSearchOpen(false); }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[var(--text-primary)]">{row.title}</span>
                        {row.ai ? (
                          <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[var(--primary)]/40 text-[var(--primary)]">
                            AI
                          </span>
                        ) : null}
                      </div>
                      <div className="text-[10px] font-mono text-[var(--text-tertiary)]">{row.subtitle}</div>
                    </Link>
                  ))}
                  {searchResults.length === 0 ? <div className="px-3 py-2 text-xs text-[var(--text-tertiary)]">No matching records.</div> : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <main className={`flex-1 min-h-0 ${isImmersiveEditor ? "overflow-hidden" : "overflow-y-auto"}`} data-testid="admin-main">
          <AdminEventsContext.Provider
            value={{ events, setEvents, eventsLoading, regions, refreshEvents }}
          >
            <Outlet />
          </AdminEventsContext.Provider>
        </main>
      </div>
      </div>

      <Dialog open={aiSearchOpen} onOpenChange={setAiSearchOpen}>
        <DialogContent
          className="max-w-2xl w-[calc(100vw-2rem)] border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-primary)] p-0 gap-0 sm:rounded-none"
          data-testid="ai-search-modal"
        >
          <DialogHeader className="px-5 pt-5 pb-0 space-y-1">
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <Sparkles size={16} className="text-[var(--primary)]" />
              AI Search
            </DialogTitle>
            <DialogDescription className="text-xs text-[var(--text-tertiary)]">
              Natural-language search across attendees, help, comms, and events.
            </DialogDescription>
          </DialogHeader>
          <div className="px-5 pb-5 pt-3">
            <AdminAiSearchPanel
              aiEnabled={aiSearchEnabled}
              eventId={isEventScope ? params.id : null}
              onNavigate={() => setAiSearchOpen(false)}
              compact
              autoFocus
            />
          </div>
        </DialogContent>
      </Dialog>

      {!isImmersiveEditor ? (
      <AivoryOneDock
        aiEnabled={aiSearchEnabled}
        eventId={isEventScope ? params.id : events.length === 1 ? events[0]?.id || null : null}
      />
      ) : null}
    </div>
  );
}
